import { useRecoilValue } from "recoil";
import { ActionsCell } from "../../Component/Common/Components/DataTable/ActionsCell";
import { DefaultCell } from "../../Component/Common/Components/DataTable/DefaultCell";
import { BEE123SalariesRole, EE123PeopleRole_Edit, EE123SalariesRole, PeopleEditRole, PlatformPeopleViewRole, SubAdminRole, UserAdminRole } from "../../Component/Common/Constants/UserRoles";
import { Organisation } from "../../Component/Model/Dto/Organisation/Organisation";
import { ActionType, TableColumn } from "../../Component/Model/Table/Table";
import { userRolesState } from "../../State/user.Atom";
import config from "../../config";

interface OrganisationRows {
    registrationName: string,
    registrationNumber: string,
    vatNumber: string,
    tradeName: string,
    actions: ActionType[];
}

interface OrganisationTable {
    columns: Array<TableColumn>;
    rows: Array<OrganisationRows>;
}

export function OrganisationTableMapper(
    dataObject: Organisation[], 
    handleEdit: (id: number) => void, 
    handleAssignUser: (organisationId: number, organisationName: string) => void, 
    openPayrollIntegration: (id: number, name: string) => void, 
    openManagePeople: (organisation: Organisation) => void, 
    openManageTraining: (organisation: Organisation) => void,
    openFetchPayrollData: (organisation: Organisation) => void, 
    hasWSPScorecardLicenses: boolean,
    enabledPayrollIntegration: boolean): OrganisationTable {
    
    const userRoles = useRecoilValue(userRolesState);

    function getActionsColumn(item: Organisation, index: number) {
        const actions: ActionType[] = [];

        if (enabledPayrollIntegration) {
            if (userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole)) {
                actions.push({
                    key: index.toString(),
                    label: 'Manage Payroll Integrations',
                    action: () => {
                        openPayrollIntegration(item.organisationId ?? -1, item.registrationName);
                    },
                    icon: {
                        color: 'primary',
                        name: 'assignment'
                    }
                });
            }
        
            if ((userRoles.includes(PeopleEditRole) || userRoles.includes(EE123PeopleRole_Edit) || userRoles.includes(BEE123SalariesRole) || userRoles.includes(EE123SalariesRole)) 
            && !userRoles.includes(SubAdminRole) && !userRoles.includes(UserAdminRole)) {
                actions.push({
                    key: index.toString(),
                    label: 'Fetch Payroll Data',
                    action: () => {
                        openFetchPayrollData(item);
                    },
                    icon: {
                        color: 'primary',
                        name: 'autorenew'
                    }
                });
            }
        }

        if (config.enable_WSPATR === 'true' && hasWSPScorecardLicenses) {
            if (userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole) || userRoles.includes(PlatformPeopleViewRole)) {
                actions.push({
                    key: index.toString(),
                    label: 'Manage WSP People',
                    action: () => {
                        openManagePeople(item);
                    },
                    icon: {
                        color: 'primary',
                        name: 'groups'
                    }
                });

                actions.push({
                    key: index.toString(),
                    label: 'Manage WSP Training',
                    action: () => {
                        openManageTraining(item);
                    },
                    icon: {
                        color: 'primary',
                        name: 'local_library'
                    }
                });
            }
        }

        if (userRoles.includes(UserAdminRole) || userRoles.includes(SubAdminRole)) {
            actions.push({
                key: `assign_${index}`,
                label: 'Assign Organisation Users',
                action: () => {
                    handleAssignUser(item.organisationId!, item.registrationName);
                },
                icon: {
                    color: 'primary',
                    name: 'person_add'
                }
            });

            actions.push({
            key: index.toString(),
            label: 'Edit Organisation Details',
            action: () => {
                handleEdit(item.organisationId!);
            },
            icon: {
                color: 'info',
                name: 'edit'
            }
        });
        }

        return actions;
    }

    const columns = [
        { Header: 'Registration Name', accessor: 'registrationName', id: 'registrationName', Cell: ({ value }: any) => <DefaultCell value={value} /> },
        { Header: 'Registration Number', accessor: 'registrationNumber', id: 'registrationNumber', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'VAT Number', accessor: 'vatNumber', id: 'vatNumber', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Trade Name', accessor: 'tradeName', id: 'tradeName', Cell: ({ value }: any) => <DefaultCell value={value} />, disableSortBy: true },
        { Header: 'Actions', accessor: 'actions', Cell: ({ value }: any) => <ActionsCell actions={value} />, disableSortBy: true },
    ];
    if (dataObject != undefined && dataObject.length > 0) {
        const rows = dataObject.map((item: Organisation, index: number) => ({
            registrationName: item.registrationName,
            registrationNumber: item.registrationNumber,
            vatNumber: item.vatNumber,
            tradeName: item.tradeName,
            actions: getActionsColumn(item, index),
        }));
        return { columns: columns, rows: rows };
    }
    else {
        return { columns: columns, rows: [] };
    }
}